import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import EmployeePicker from "@/components/EmployeePicker";
import { toast } from "sonner";
import { FileText, Download, Building2, Users, GitCompare, Layers, HandCoins, CalendarCheck, Receipt, Award, CalendarRange, Banknote, Shield, BarChart3, Landmark, HeartPulse } from "lucide-react";
import DeptCostTrend from "@/components/DeptCostTrend";
import { NhfReport, HmoReport } from "@/components/reports/NhfHmoReports";

function fmt(n) {
    if (n == null) return "₦0";
    return "₦" + Number(n).toLocaleString("en-NG", { maximumFractionDigits: 2 });
}

// Return the Tailwind colour class for a positive/negative/zero delta.
// Keeps long ternaries out of JSX for readability.
function deltaCls(delta) {
    if (delta > 0) return "text-green-700";
    if (delta < 0) return "text-red-700";
    return "text-ink/40";
}

const TABS = [
    { key: "paysheet", label: "Paysheet", icon: Layers },
    { key: "paye", label: "PAYE Remittance", icon: FileText },
    { key: "pension", label: "Pension Remittance", icon: Building2 },
    { key: "nhf", label: "NHF Remittance", icon: Landmark },
    { key: "hmo", label: "HMO Deductions", icon: HeartPulse },
    { key: "movement", label: "Staff Movement", icon: Users },
    { key: "attendance", label: "Attendance", icon: CalendarCheck },
    { key: "comparison", label: "Payroll Comparison", icon: GitCompare },
    { key: "dept-cost", label: "Department Cost Trend", icon: BarChart3 },
    { key: "loans", label: "Loans & Advances", icon: HandCoins },
    { key: "leaves", label: "Leaves", icon: CalendarRange },
    { key: "settlements", label: "F&F Settlements", icon: Receipt },
    { key: "statutory", label: "ITF & NSITF", icon: Banknote },
    { key: "gla", label: "Group Life Assurance", icon: Shield },
    { key: "performance", label: "Performance & Ranking", icon: Award },
];

// Stable column/header presets — hoisted out so React props get the same reference on every render
// (prevents wasted re-renders of memoised sub-components).
const MOVEMENT_JOINERS_COLS = ["joining_date", "employee_no", "employee_name", "department", "salary"];
const MOVEMENT_JOINERS_HEADERS = ["Date", "Emp No", "Name", "Dept", "Salary"];
const MOVEMENT_LEAVERS_COLS = ["type", "date", "employee_no", "employee_name", "department"];
const MOVEMENT_LEAVERS_HEADERS = ["Type", "Date", "Emp No", "Name", "Dept"];
const MOVEMENT_INCREMENTS_COLS = ["date", "employee_no", "employee_name", "department", "previous_salary", "new_salary", "reason"];
const MOVEMENT_INCREMENTS_HEADERS = ["Date", "Emp No", "Name", "Dept", "Previous", "New", "Reason"];
const SALARY_MONEY = ["salary"];
const INCREMENT_MONEY = ["previous_salary", "new_salary"];
const PEOPLE_COMPACT_COLS = ["employee_no", "employee_name", "department", "salary"];
const PEOPLE_COMPACT_HEADERS = ["Emp No", "Name", "Dept", "Salary"];

export default function Reports() {
    const [tab, setTab] = useState("paysheet");
    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="reports-page">
            <div>
                <div className="overline">Compliance & analytics</div>
                <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                    Reports
                </h1>
                <p className="mt-3 text-sm text-ink/60 max-w-xl">
                    Generate statutory remittance reports and lifecycle movement summaries.
                </p>
            </div>

            <div className="mt-10 flex items-center gap-1 border-b border-ink/10 overflow-x-auto" data-testid="reports-tab-bar">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`flex items-center gap-2 px-5 py-3 text-sm border-b-2 -mb-px whitespace-nowrap shrink-0 ${
                                tab === t.key ? "border-klein text-ink font-medium" : "border-transparent text-ink/50 hover:text-ink"
                            }`}
                            data-testid={`reports-tab-${t.key}`}
                        >
                            <Icon className="w-4 h-4" />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="mt-8">
                {tab === "paysheet" && <PaysheetReport />}
                {tab === "paye" && <PayeReport />}
                {tab === "pension" && <PensionReport />}
                {tab === "nhf" && <NhfReport />}
                {tab === "hmo" && <HmoReport />}
                {tab === "movement" && <MovementReport />}
                {tab === "attendance" && <AttendanceReport />}
                {tab === "comparison" && <ComparisonReport />}
                {tab === "dept-cost" && <DeptCostTrend />}
                {tab === "loans" && <LoansReport />}
                {tab === "leaves" && <LeavesReport />}
                {tab === "settlements" && <SettlementsReport />}
                {tab === "statutory" && <StatutoryReport />}
                {tab === "gla" && <GLAReport />}
                {tab === "performance" && <PerformanceReport />}
            </div>
        </div>
    );
}

function defaultPeriod() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function PeriodPicker({ value, onChange, onLoad, loading }) {
    return (
        <div className="bg-white border border-ink/10 p-5 flex items-end gap-4 flex-wrap" data-testid="period-picker">
            <div>
                <label className="overline block mb-2">Pay period</label>
                <input type="month" value={value} onChange={(e) => onChange(e.target.value)} className="border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none" data-testid="period-input" />
            </div>
            <button onClick={onLoad} disabled={loading} className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="period-load-button">
                {loading ? "Loading…" : "Generate"}
            </button>
        </div>
    );
}

function exportCSV(filename, headers, rows) {
    const csv = [headers.join(",")].concat(rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
}

function PayeReport() {
    const [period, setPeriod] = useState(defaultPeriod());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/paye-remittance", { params: { period } });
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "No payroll for period");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const onExport = () => {
        if (!data) return;
        const rows = (data.rows || []).map((r) => [r.employee_no, r.employee_name, r.department, r.tax_id, r.gross, r.paye]);
        exportCSV(
            `paye_remittance_${period}.csv`,
            ["Employee No", "Name", "Department", "Tax ID", "Gross", "PAYE"],
            rows,
        );
    };

    const onDownloadPdf = async () => {
        try {
            const res = await api.get("/reports/paye-remittance.pdf", { params: { period }, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url; a.download = `paye-remittance-${period}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("PDF downloaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Download failed");
        }
    };

    const onDownloadStatutoryTemplate = async () => {
        try {
            const res = await api.get("/reports/paye-template.csv", { params: { period }, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url; a.download = `lirs-paye-schedule-${period}.csv`;
            document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);
            toast.success("LIRS PAYE schedule downloaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Download failed");
        }
    };

    return (
        <div className="space-y-6">
            <PeriodPicker value={period} onChange={setPeriod} onLoad={load} loading={loading} />
            {data && (
                <div className="space-y-6" data-testid="paye-report-result">
                    <div className="bg-klein text-white p-6 flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <div className="overline !text-white/70">Total PAYE remittance</div>
                            <div className="mt-2 font-display font-black text-3xl tabular-nums">{fmt(data.grand_total)}</div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <button onClick={onDownloadPdf} className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2.5 text-sm font-medium hover:bg-white/30" data-testid="paye-pdf-button" title="Download the PAYE remittance summary as PDF">
                                <Download className="w-4 h-4" /> PDF
                            </button>
                            <button onClick={onDownloadStatutoryTemplate} className="inline-flex items-center gap-2 border border-white/30 text-white px-4 py-2.5 text-sm font-medium hover:bg-white/10" data-testid="paye-statutory-template-button" title="Per-employee PAYE schedule ready to upload on the LIRS eTax portal">
                                <Download className="w-4 h-4" /> LIRS schedule (.csv)
                            </button>
                            <button onClick={onExport} className="inline-flex items-center gap-2 bg-white text-klein px-4 py-2.5 text-sm font-medium hover:bg-bone" data-testid="paye-export-button">
                                <Download className="w-4 h-4" /> Export CSV
                            </button>
                        </div>
                    </div>
                    {(() => {
                        const allRows = data.rows || [];
                        return (
                            <div className="bg-white border border-ink/10 overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                    <thead><tr className="border-b border-ink/10 bg-bone/40">
                                        <th className="text-left overline px-4 py-2">Emp No</th>
                                        <th className="text-left overline px-4 py-2">Name</th>
                                        <th className="text-left overline px-4 py-2">Dept</th>
                                        <th className="text-left overline px-4 py-2">Tax ID</th>
                                        <th className="text-right overline px-4 py-2">Gross</th>
                                        <th className="text-right overline px-4 py-2">PAYE</th>
                                    </tr></thead>
                                    <tbody>
                                        {allRows.map((r, i) => (
                                            <tr key={r.employee_id || r.employee_no || `${r.employee_name}-${i}`} className="border-b border-ink/5">
                                                <td className="px-4 py-2 text-ink/70">{r.employee_no || "—"}</td>
                                                <td className="px-4 py-2 text-ink">{r.employee_name}</td>
                                                <td className="px-4 py-2 text-ink/70">{r.department || "—"}</td>
                                                <td className="px-4 py-2 text-ink/70">{r.tax_id || "—"}</td>
                                                <td className="px-4 py-2 text-right tabular-nums">{fmt(r.gross)}</td>
                                                <td className="px-4 py-2 text-right tabular-nums font-medium">{fmt(r.paye)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })()}
                </div>
            )}
        </div>
    );
}

function PensionReport() {
    const [period, setPeriod] = useState(defaultPeriod());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/pension-remittance", { params: { period } });
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "No payroll for period");
            setData(null);
        } finally { setLoading(false); }
    };

    const onExport = () => {
        if (!data) return;
        const rows = (data.rows || []).map((r) => [r.employee_no, r.employee_name, r.department, r.pension_id, r.pfa, r.gross, r.pensionable_pay, r.employee_pension, r.employer_pension, r.total]);
        exportCSV(
            `pension_remittance_${period}.csv`,
            ["Employee No", "Name", "Department", "Pension ID", "PFA", "Gross", "Pensionable pay", "Employee 8%", "Employer 10%", "Total"],
            rows,
        );
    };

    const onDownloadPdf = async () => {
        try {
            const res = await api.get("/reports/pension-remittance.pdf", { params: { period }, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url; a.download = `pension-remittance-${period}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("PDF downloaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Download failed");
        }
    };

    const onDownloadStatutoryTemplate = async () => {
        try {
            const res = await api.get("/reports/pension-template.csv", { params: { period }, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url; a.download = `paypen-pension-schedule-${period}.csv`;
            document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);
            toast.success("PayPen pension schedule downloaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Download failed");
        }
    };

    return (
        <div className="space-y-6">
            <PeriodPicker value={period} onChange={setPeriod} onLoad={load} loading={loading} />
            {data && (
                <div className="space-y-6" data-testid="pension-report-result">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10">
                        <div className="bg-white p-5">
                            <div className="overline">Employee 8%</div>
                            <div className="font-display font-black text-2xl mt-2 tabular-nums">{fmt(data.grand_employee_total)}</div>
                        </div>
                        <div className="bg-white p-5">
                            <div className="overline">Employer 10%</div>
                            <div className="font-display font-black text-2xl mt-2 tabular-nums">{fmt(data.grand_employer_total)}</div>
                        </div>
                        <div className="bg-klein text-white p-5 flex items-center justify-between gap-2 flex-wrap">
                            <div>
                                <div className="overline !text-white/70">Total</div>
                                <div className="font-display font-black text-2xl mt-2 tabular-nums">{fmt(data.grand_total)}</div>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <button onClick={onDownloadPdf} className="inline-flex items-center gap-2 bg-white/20 text-white px-3 py-2 text-xs font-medium hover:bg-white/30" data-testid="pension-pdf-button">
                                    <Download className="w-3.5 h-3.5" /> PDF
                                </button>
                                <button onClick={onDownloadStatutoryTemplate} className="inline-flex items-center gap-2 border border-white/30 text-white px-3 py-2 text-xs font-medium hover:bg-white/10" data-testid="pension-statutory-template-button" title="Per-employee pension schedule ready to upload on PayPen">
                                    <Download className="w-3.5 h-3.5" /> PayPen schedule (.csv)
                                </button>
                                <button onClick={onExport} className="inline-flex items-center gap-2 bg-white text-klein px-3 py-2 text-xs font-medium hover:bg-bone" data-testid="pension-export-button">
                                    <Download className="w-3.5 h-3.5" /> CSV
                                </button>
                            </div>
                        </div>
                    </div>
                    {(() => {
                        const allRows = data.rows || [];
                        return (
                            <div className="bg-white border border-ink/10 overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                    <thead><tr className="border-b border-ink/10 bg-bone/40">
                                        <th className="text-left overline px-3 py-2">Emp No</th>
                                        <th className="text-left overline px-3 py-2">Name</th>
                                        <th className="text-left overline px-3 py-2">PFA</th>
                                        <th className="text-left overline px-3 py-2">Pension ID</th>
                                        <th className="text-right overline px-3 py-2">Gross</th>
                                        <th className="text-right overline px-3 py-2">Pensionable pay</th>
                                        <th className="text-right overline px-3 py-2">Emp 8%</th>
                                        <th className="text-right overline px-3 py-2">Emplr 10%</th>
                                        <th className="text-right overline px-3 py-2">Total</th>
                                    </tr></thead>
                                    <tbody>
                                        {allRows.map((r, i) => (
                                            <tr key={r.employee_id || r.employee_no || `${r.employee_name}-${i}`} className="border-b border-ink/5">
                                                <td className="px-3 py-2 text-ink/70">{r.employee_no || "—"}</td>
                                                <td className="px-3 py-2 text-ink">{r.employee_name}</td>
                                                <td className="px-3 py-2 text-ink/70">{r.pfa || "—"}</td>
                                                <td className="px-3 py-2 text-ink/70">{r.pension_id || "—"}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.gross)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums" data-testid={`pension-base-${r.employee_no || i}`}>{fmt(r.pensionable_pay)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.employee_pension)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.employer_pension)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums font-medium">{fmt(r.total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })()}
                </div>
            )}
        </div>
    );
}

function MovementReport() {
    const today = new Date();
    const firstOfYear = new Date(today.getFullYear(), 0, 1);
    const [start, setStart] = useState(firstOfYear.toISOString().slice(0, 10));
    const [end, setEnd] = useState(today.toISOString().slice(0, 10));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/staff-movement", { params: { start, end } });
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not generate report");
            setData(null);
        } finally { setLoading(false); }
    };

    const onExport = () => {
        if (!data) return;
        const rows = [];
        data.joiners.forEach((j) => rows.push(["Joiner", j.joining_date, j.employee_no, j.employee_name, j.department, "", j.salary]));
        data.leavers.forEach((l) => rows.push([l.type || "Leaver", l.date, l.employee_no, l.employee_name, l.department, "", l.salary]));
        data.increments.forEach((i) => rows.push(["Increment", i.date, i.employee_no, i.employee_name, i.department, i.previous_salary, i.new_salary]));
        exportCSV(
            `staff_movement_${start}_${end}.csv`,
            ["Type", "Date", "Employee No", "Name", "Department", "Previous", "Salary"],
            rows,
        );
    };

    return (
        <div className="space-y-6">
            <div className="bg-white border border-ink/10 p-5 flex items-end gap-4 flex-wrap">
                <div>
                    <label className="overline block mb-2">Start</label>
                    <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none" data-testid="movement-start" />
                </div>
                <div>
                    <label className="overline block mb-2">End</label>
                    <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none" data-testid="movement-end" />
                </div>
                <button onClick={load} disabled={loading} className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="movement-load-button">
                    {loading ? "Loading…" : "Generate"}
                </button>
                {data && (
                    <button onClick={onExport} className="ml-auto inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5" data-testid="movement-export-button">
                        <Download className="w-4 h-4" /> Export CSV
                    </button>
                )}
            </div>

            {data && (
                <div className="space-y-6" data-testid="movement-report-result">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10">
                        <div className="bg-white p-5"><div className="overline">Joiners</div><div className="font-display font-black text-3xl mt-2 tabular-nums text-green-700">{data.totals.joiners}</div></div>
                        <div className="bg-white p-5"><div className="overline">Leavers</div><div className="font-display font-black text-3xl mt-2 tabular-nums text-red-700">{data.totals.leavers}</div></div>
                        <div className="bg-white p-5"><div className="overline">Increments</div><div className="font-display font-black text-3xl mt-2 tabular-nums text-klein">{data.totals.increments}</div></div>
                    </div>

                    <MovementSection title="Joiners" rows={data.joiners} cols={MOVEMENT_JOINERS_COLS} headers={MOVEMENT_JOINERS_HEADERS} accent="green" testid="joiners-table" money={SALARY_MONEY} />
                    <MovementSection title="Leavers" rows={data.leavers} cols={MOVEMENT_LEAVERS_COLS} headers={MOVEMENT_LEAVERS_HEADERS} accent="red" testid="leavers-table" />
                    <MovementSection title="Salary increments" rows={data.increments} cols={MOVEMENT_INCREMENTS_COLS} headers={MOVEMENT_INCREMENTS_HEADERS} accent="klein" testid="increments-table" money={INCREMENT_MONEY} />
                </div>
            )}
        </div>
    );
}

function MovementSection({ title, rows, cols, headers, accent, testid, money = [] }) {
    if (!rows || rows.length === 0) return null;
    return (
        <div className="bg-white border border-ink/10" data-testid={testid}>
            <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg text-ink">{title} <span className="text-ink/40 text-sm font-normal">· {rows.length}</span></div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead><tr className="border-b border-ink/10 bg-bone/40">
                        {headers.map((h) => <th key={h} className={`overline px-3 py-2 ${money.includes(cols[headers.indexOf(h)]) ? "text-right" : "text-left"}`}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={r.employee_id || r.id || r.employee_no || r.employee_name || `row-${i}`} className="border-b border-ink/5">
                                {cols.map((c) => (
                                    <td key={c} className={`px-3 py-2 ${money.includes(c) ? "text-right tabular-nums" : "text-ink/80"}`}>
                                        {money.includes(c) ? fmt(r[c]) : (r[c] || "—")}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function ComparisonReport() {
    const [period, setPeriod] = useState(defaultPeriod());
    const [previous, setPrevious] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = previous ? { period, previous } : { period };
            const { data } = await api.get("/reports/payroll-comparison", { params });
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not generate comparison");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const onExport = () => {
        if (!data) return;
        const rows = [];
        data.joiners.forEach((j) => rows.push(["Joiner", j.employee_no, j.employee_name, j.department, "", j.salary, ""]));
        data.leavers.forEach((l) => rows.push(["Leaver", l.employee_no, l.employee_name, l.department, l.salary, "", ""]));
        data.increments.forEach((i) => rows.push(["Increment", i.employee_no, i.employee_name, i.department, i.previous_salary, i.new_salary, i.difference]));
        data.decrements.forEach((d) => rows.push(["Decrement", d.employee_no, d.employee_name, d.department, d.previous_salary, d.new_salary, d.difference]));
        exportCSV(
            `payroll_comparison_${data.previous_period}_vs_${data.current_period}.csv`,
            ["Type", "Emp No", "Name", "Department", "Previous Salary", "New Salary", "Difference"],
            rows,
        );
    };

    return (
        <div className="space-y-6">
            <div className="bg-white border border-ink/10 p-5 flex items-end gap-4 flex-wrap" data-testid="comparison-picker">
                <div>
                    <label className="overline block mb-2">Current period</label>
                    <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none" data-testid="comparison-current" />
                </div>
                <div>
                    <label className="overline block mb-2">Compare to</label>
                    <input type="month" value={previous} onChange={(e) => setPrevious(e.target.value)} placeholder="Auto: previous run" className="border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none" data-testid="comparison-previous" />
                </div>
                <button onClick={load} disabled={loading} className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="comparison-load-button">
                    {loading ? "Comparing…" : "Compare"}
                </button>
                {data && (
                    <button onClick={onExport} className="ml-auto inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5" data-testid="comparison-export-button">
                        <Download className="w-4 h-4" /> Export CSV
                    </button>
                )}
                <p className="w-full text-xs text-ink/50 mt-1">
                    Leave &quot;Compare to&quot; empty to auto-pick the previous payroll run.
                </p>
            </div>

            {data && (
                <div className="space-y-6" data-testid="comparison-result">
                    <div className="bg-white border border-ink/10 p-6">
                        <div className="text-sm text-ink/60">
                            Comparing <span className="font-semibold text-ink">{data.current_period}</span> vs <span className="font-semibold text-ink">{data.previous_period}</span>
                        </div>
                    </div>

                    {/* Summary cards */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-ink/10">
                        <SummaryCard label="Joiners" value={data.summary.joiners} accent="text-green-700" />
                        <SummaryCard label="Leavers" value={data.summary.leavers} accent="text-red-700" />
                        <SummaryCard label="Increments" value={data.summary.increments} accent="text-klein" />
                        <SummaryCard label="Decrements" value={data.summary.decrements} accent="text-amber-700" />
                        <SummaryCard label="Unchanged" value={data.summary.unchanged} accent="text-ink/60" />
                    </div>

                    {/* Totals comparison */}
                    <div className="bg-white border border-ink/10 overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="border-b-2 border-ink/20 bg-bone">
                                    <th className="text-left overline px-4 py-3">Metric</th>
                                    <th className="text-right overline px-4 py-3">{data.previous_period}</th>
                                    <th className="text-right overline px-4 py-3">{data.current_period}</th>
                                    <th className="text-right overline px-4 py-3">Δ Change</th>
                                    <th className="text-right overline px-4 py-3">% Change</th>
                                </tr>
                            </thead>
                            <tbody>
                                <TotalRow label="Headcount" prev={data.totals.previous.headcount} cur={data.totals.current.headcount} delta={data.totals.delta.headcount} pct={data.totals.pct_change.headcount} money={false} />
                                <TotalRow label="Gross salary" prev={data.totals.previous.gross} cur={data.totals.current.gross} delta={data.totals.delta.gross} pct={data.totals.pct_change.gross} />
                                <TotalRow label="PAYE" prev={data.totals.previous.paye} cur={data.totals.current.paye} delta={data.totals.delta.paye} pct={data.totals.pct_change.paye} />
                                <TotalRow label="Pension" prev={data.totals.previous.pension} cur={data.totals.current.pension} delta={data.totals.delta.pension} pct={data.totals.pct_change.pension} />
                                <TotalRow label="Loan deductions" prev={data.totals.previous.loan_deduction} cur={data.totals.current.loan_deduction} delta={data.totals.delta.loan_deduction} pct={data.totals.pct_change.loan_deduction} />
                                <TotalRow label="Recurring deductions" prev={data.totals.previous.recurring_deduction || 0} cur={data.totals.current.recurring_deduction || 0} delta={data.totals.delta.recurring_deduction || 0} pct={data.totals.pct_change.recurring_deduction || 0} />
                                <TotalRow label="Net pay" prev={data.totals.previous.net} cur={data.totals.current.net} delta={data.totals.delta.net} pct={data.totals.pct_change.net} highlight />
                            </tbody>
                        </table>
                    </div>

                    {/* By department */}
                    {data.by_department.length > 0 && (
                        <div className="bg-white border border-ink/10" data-testid="comparison-by-department">
                            <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg text-ink">By department</div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                    <thead>
                                        <tr className="border-b border-ink/10 bg-bone/40">
                                            <th className="text-left overline px-4 py-2">Department</th>
                                            <th className="text-right overline px-4 py-2">Prev HC</th>
                                            <th className="text-right overline px-4 py-2">Cur HC</th>
                                            <th className="text-right overline px-4 py-2">Δ HC</th>
                                            <th className="text-right overline px-4 py-2">Prev Gross</th>
                                            <th className="text-right overline px-4 py-2">Cur Gross</th>
                                            <th className="text-right overline px-4 py-2">Δ Gross</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.by_department.map((c) => (
                                            <tr key={c.department} className="border-b border-ink/5">
                                                <td className="px-4 py-2 text-ink font-medium">{c.department}</td>
                                                <td className="px-4 py-2 text-right tabular-nums">{c.previous_headcount}</td>
                                                <td className="px-4 py-2 text-right tabular-nums">{c.current_headcount}</td>
                                                <td className={`px-4 py-2 text-right tabular-nums ${deltaCls(c.headcount_change)}`}>
                                                    {c.headcount_change > 0 ? "+" : ""}{c.headcount_change}
                                                </td>
                                                <td className="px-4 py-2 text-right tabular-nums">{fmt(c.previous_gross)}</td>
                                                <td className="px-4 py-2 text-right tabular-nums">{fmt(c.current_gross)}</td>
                                                <td className={`px-4 py-2 text-right tabular-nums ${deltaCls(c.gross_change)}`}>
                                                    {c.gross_change > 0 ? "+" : ""}{fmt(c.gross_change)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <PeopleSection title="Joiners" rows={data.joiners} testid="joiners-list" money={SALARY_MONEY}
                        cols={PEOPLE_COMPACT_COLS}
                        headers={PEOPLE_COMPACT_HEADERS} />
                    <PeopleSection title="Leavers" rows={data.leavers} testid="leavers-list" money={SALARY_MONEY}
                        cols={PEOPLE_COMPACT_COLS}
                        headers={PEOPLE_COMPACT_HEADERS} />
                    <IncrementSection title="Salary increments" rows={data.increments} accent="green" testid="increments-list" />
                    <IncrementSection title="Salary decrements" rows={data.decrements} accent="red" testid="decrements-list" />
                </div>
            )}
        </div>
    );
}

function SummaryCard({ label, value, accent }) {
    return (
        <div className="bg-white p-5">
            <div className="overline">{label}</div>
            <div className={`font-display font-black text-3xl mt-2 tabular-nums ${accent}`}>{value}</div>
        </div>
    );
}

function TotalRow({ label, prev, cur, delta, pct, money = true, highlight = false }) {
    const cls = deltaCls(delta);
    const F = money ? fmt : (n) => Number(n).toLocaleString();
    return (
        <tr className={`border-b border-ink/5 ${highlight ? "bg-klein/5 font-medium" : ""}`}>
            <td className="px-4 py-3 text-ink">{label}</td>
            <td className="px-4 py-3 text-right tabular-nums">{F(prev)}</td>
            <td className="px-4 py-3 text-right tabular-nums">{F(cur)}</td>
            <td className={`px-4 py-3 text-right tabular-nums ${cls}`}>
                {delta > 0 ? "+" : ""}{F(delta)}
            </td>
            <td className={`px-4 py-3 text-right tabular-nums ${cls}`}>
                {pct > 0 ? "+" : ""}{Number(pct).toFixed(2)}%
            </td>
        </tr>
    );
}

function PeopleSection({ title, rows, cols, headers, testid, money = [] }) {
    if (!rows || rows.length === 0) return null;
    return (
        <div className="bg-white border border-ink/10" data-testid={testid}>
            <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg text-ink">
                {title} <span className="text-ink/40 text-sm font-normal">· {rows.length}</span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead><tr className="border-b border-ink/10 bg-bone/40">
                        {headers.map((h) => <th key={h} className={`overline px-3 py-2 ${money.includes(cols[headers.indexOf(h)]) ? "text-right" : "text-left"}`}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={r.employee_id || r.id || r.employee_no || r.employee_name || `row-${i}`} className="border-b border-ink/5">
                                {cols.map((c) => (
                                    <td key={c} className={`px-3 py-2 ${money.includes(c) ? "text-right tabular-nums" : "text-ink/80"}`}>
                                        {money.includes(c) ? fmt(r[c]) : (r[c] || "—")}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function IncrementSection({ title, rows, accent, testid }) {
    if (!rows || rows.length === 0) return null;
    const colorCls = accent === "red" ? "text-red-700" : "text-green-700";
    return (
        <div className="bg-white border border-ink/10" data-testid={testid}>
            <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg text-ink">
                {title} <span className="text-ink/40 text-sm font-normal">· {rows.length}</span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                    <thead><tr className="border-b border-ink/10 bg-bone/40">
                        <th className="text-left overline px-3 py-2">Emp No</th>
                        <th className="text-left overline px-3 py-2">Name</th>
                        <th className="text-left overline px-3 py-2">Dept</th>
                        <th className="text-right overline px-3 py-2">Previous</th>
                        <th className="text-right overline px-3 py-2">New</th>
                        <th className="text-right overline px-3 py-2">Δ</th>
                        <th className="text-right overline px-3 py-2">%</th>
                    </tr></thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={r.employee_id || r.employee_no || `${r.employee_name}-${i}`} className="border-b border-ink/5">
                                <td className="px-3 py-2 text-ink/70">{r.employee_no || "—"}</td>
                                <td className="px-3 py-2 text-ink">{r.employee_name}</td>
                                <td className="px-3 py-2 text-ink/70">{r.department || "—"}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.previous_salary)}</td>
                                <td className="px-3 py-2 text-right tabular-nums font-medium">{fmt(r.new_salary)}</td>
                                <td className={`px-3 py-2 text-right tabular-nums ${colorCls}`}>{r.difference > 0 ? "+" : ""}{fmt(r.difference)}</td>
                                <td className={`px-3 py-2 text-right tabular-nums ${colorCls}`}>{r.pct_change > 0 ? "+" : ""}{Number(r.pct_change).toFixed(2)}%</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function PaysheetReport() {
    const [period, setPeriod] = useState(defaultPeriod());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/paysheet", { params: { period } });
            setData(data);
            // expand all divisions by default
            const init = {};
            (data.divisions || []).forEach((d) => { init[d.division] = true; });
            setExpanded(init);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not generate paysheet");
            setData(null);
        } finally { setLoading(false); }
    };

    const onExport = () => {
        if (!data) return;
        const rows = [];
        for (const d of (data.divisions || [])) {
            for (const r of d.rows) {
                rows.push([
                    d.division,
                    r.employee_no, r.employee_name, r.department, r.cadre, r.category, r.group,
                    r.basic_salary, r.housing_allowance, r.transport_allowance, r.allowances,
                    r.gross_salary, r.paye, r.pension, r.loan_deduction,
                    r.recurring_deduction || 0,
                    (r.recurring_refs || []).map((x) => `${x.label}:${x.amount}`).join("; "),
                    r.other_deductions, r.net_pay,
                    r.bank_name, r.bank_account_number,
                ]);
            }
        }
        exportCSV(
            `paysheet_${data.period}.csv`,
            ["Department", "Emp No", "Name", "Dept", "Cadre", "Category", "Group",
             "Basic", "Housing", "Transport", "Allowances", "Gross", "PAYE", "Pension",
             "Loan", "Recurring", "Recurring Breakdown", "Other", "Net", "Bank", "Account No"],
            rows,
        );
    };

    const onDownloadPdf = async () => {
        try {
            const res = await api.get("/reports/paysheet.pdf", { params: { period }, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url; a.download = `paysheet-${period}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Paysheet PDF downloaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Download failed");
        }
    };

    return (
        <div className="space-y-6">
            <PeriodPicker value={period} onChange={setPeriod} onLoad={load} loading={loading} />
            {data && (
                <div className="space-y-6" data-testid="paysheet-result">
                    <div className="bg-klein text-white p-6 flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <div className="overline !text-white/70">Paysheet · {data.period} · {String(data.status).toUpperCase()}</div>
                            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-2 text-sm">
                                <Stat label="Headcount" value={data.grand_totals.headcount} />
                                <Stat label="Gross" value={fmt(data.grand_totals.gross)} />
                                <Stat label="Total Deductions" value={fmt(data.grand_totals.paye + data.grand_totals.pension + data.grand_totals.loan_deduction + (data.grand_totals.recurring_deduction || 0) + data.grand_totals.other_deductions)} />
                                <Stat label="Net Pay" value={fmt(data.grand_totals.net)} highlight />
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <button onClick={onDownloadPdf} className="inline-flex items-center gap-2 border border-white/30 text-white px-4 py-2.5 text-sm font-medium hover:bg-white/10" data-testid="paysheet-pdf-button" title="Print-ready paysheet PDF grouped by department">
                                <Download className="w-4 h-4" /> PDF
                            </button>
                            <button onClick={onExport} className="inline-flex items-center gap-2 bg-white text-klein px-4 py-2.5 text-sm font-medium hover:bg-bone" data-testid="paysheet-export-button">
                                <Download className="w-4 h-4" /> Export CSV
                            </button>
                        </div>
                    </div>

                    {/* Flat divisions across all employees (no company grouping) */}
                    {(data.divisions || []).map((d) => (
                        <div key={d.division} className="bg-white border border-ink/10" data-testid={`paysheet-division-${d.division}`}>
                            <div className="px-6 py-3 bg-bone/30 flex items-center justify-between">
                                <div className="font-medium text-ink">
                                    {d.division}
                                    <span className="text-ink/50 text-xs font-normal ml-2">({d.totals.headcount} staff)</span>
                                </div>
                                <div className="text-sm text-ink/70 tabular-nums">
                                    Gross <span className="font-semibold text-ink">{fmt(d.totals.gross)}</span>
                                    <span className="mx-3 text-ink/30">·</span>
                                    Net <span className="font-semibold text-klein">{fmt(d.totals.net)}</span>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs border-collapse">
                                    <thead>
                                        <tr className="border-b border-ink/10 bg-white">
                                            <th className="text-left overline px-3 py-2">Emp No</th>
                                            <th className="text-left overline px-3 py-2">Name</th>
                                            <th className="text-left overline px-3 py-2 hidden xl:table-cell">Dept</th>
                                            <th className="text-right overline px-3 py-2">Basic</th>
                                            <th className="text-right overline px-3 py-2">Housing</th>
                                            <th className="text-right overline px-3 py-2">Transport</th>
                                            <th className="text-right overline px-3 py-2 hidden md:table-cell">Allow</th>
                                            <th className="text-right overline px-3 py-2">Gross</th>
                                            <th className="text-right overline px-3 py-2">PAYE</th>
                                            <th className="text-right overline px-3 py-2">Pension</th>
                                            <th className="text-right overline px-3 py-2 hidden lg:table-cell">Loan</th>
                                            <th className="text-right overline px-3 py-2 hidden lg:table-cell" title="Recurring deductions (cooperative dues, etc.)">Recur.</th>
                                            <th className="text-right overline px-3 py-2 bg-klein/10">Net</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {d.rows.map((r, i) => (
                                            <tr key={r.employee_id || r.employee_no || `${r.employee_name}-${i}`} className="border-b border-ink/5">
                                                <td className="px-3 py-2 text-ink/70">{r.employee_no || "—"}</td>
                                                <td className="px-3 py-2 text-ink">{r.employee_name}</td>
                                                <td className="px-3 py-2 text-ink/70 hidden xl:table-cell">{r.department || "—"}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.basic_salary)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.housing_allowance)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.transport_allowance)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums hidden md:table-cell">{fmt(r.allowances)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(r.gross_salary)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.paye)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.pension)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums hidden lg:table-cell">{fmt(r.loan_deduction)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums hidden lg:table-cell">{fmt(r.recurring_deduction || 0)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums font-semibold text-klein bg-klein/10">{fmt(r.net_pay)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, highlight }) {
    return (
        <div>
            <div className="overline !text-white/70">{label}</div>
            <div className={`font-display font-black text-2xl tabular-nums ${highlight ? "text-white" : "text-white/90"}`}>{value}</div>
        </div>
    );
}

function LoansReport() {
    const [type, setType] = useState("all");        // all | loan | advance
    const [status, setStatus] = useState("all");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (type !== "all") params.type = type;
            if (status !== "all") params.status = status;
            const { data } = await api.get("/reports/loans-advances", { params });
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not generate report");
            setData(null);
        } finally { setLoading(false); }
    }, [type, status]);

    useEffect(() => { load(); }, [load]);

    const onExport = () => {
        if (!data) return;
        const rows = data.rows.map((r) => [
            r.type, r.status, r.employee_no, r.employee_name, r.department,
            r.amount, r.deducted, r.balance, r.monthly_installment, r.tenure_months,
            r.paid_installments, r.created_at, r.approved_at, r.reason,
        ]);
        exportCSV(
            `loans_advances.csv`,
            ["Type", "Status", "Emp No", "Name", "Dept", "Amount", "Deducted", "Balance",
             "Monthly", "Tenure", "Paid Installments", "Created", "Approved", "Reason"],
            rows,
        );
    };

    const onDownloadPdf = async () => {
        try {
            const params = { type: type === "all" ? undefined : type, status: status === "all" ? undefined : status };
            const res = await api.get("/reports/loans-advances.pdf", { params, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url; a.download = "loans-advances.pdf";
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("PDF downloaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Download failed");
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-white border border-ink/10 p-5 flex items-end gap-4 flex-wrap" data-testid="loans-report-filters">
                <div>
                    <label className="overline block mb-2">Type</label>
                    <select value={type} onChange={(e) => setType(e.target.value)} className="border border-ink/15 px-3 py-2.5 text-sm bg-transparent focus:border-klein focus:outline-none" data-testid="loans-report-type">
                        <option value="all">All</option>
                        <option value="loan">Loans</option>
                        <option value="advance">Salary advances</option>
                    </select>
                </div>
                <div>
                    <label className="overline block mb-2">Status</label>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-ink/15 px-3 py-2.5 text-sm bg-transparent focus:border-klein focus:outline-none" data-testid="loans-report-status">
                        <option value="all">All</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="disbursed">Disbursed</option>
                        <option value="completed">Completed</option>
                        <option value="rejected">Rejected</option>
                    </select>
                </div>
                {data && (
                    <div className="ml-auto flex items-center gap-2">
                        <button onClick={onDownloadPdf} className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5" data-testid="loans-report-pdf">
                            <Download className="w-4 h-4" /> PDF
                        </button>
                        <button onClick={onExport} className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5" data-testid="loans-report-export">
                            <Download className="w-4 h-4" /> Export CSV
                        </button>
                    </div>
                )}
            </div>

            {loading && <div className="text-sm text-ink/40">Loading…</div>}

            {data && (
                <div className="space-y-6" data-testid="loans-report-result">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10">
                        <div className="bg-white p-5"><div className="overline">Records</div><div className="font-display font-black text-3xl mt-2 tabular-nums">{data.totals.count}</div></div>
                        <div className="bg-white p-5"><div className="overline">Active</div><div className="font-display font-black text-3xl mt-2 tabular-nums text-klein">{data.totals.active}</div></div>
                        <div className="bg-white p-5"><div className="overline">Total disbursed</div><div className="font-display font-black text-2xl mt-2 tabular-nums">{fmt(data.totals.total_amount)}</div></div>
                        <div className="bg-white p-5"><div className="overline">Outstanding balance</div><div className="font-display font-black text-2xl mt-2 tabular-nums text-amber-700">{fmt(data.totals.total_balance)}</div></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white border border-ink/10">
                            <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg">By type</div>
                            <table className="w-full text-sm">
                                <thead><tr className="border-b border-ink/10 bg-bone/40">
                                    <th className="text-left overline px-4 py-2">Type</th>
                                    <th className="text-right overline px-4 py-2">Count</th>
                                    <th className="text-right overline px-4 py-2">Disbursed</th>
                                    <th className="text-right overline px-4 py-2">Balance</th>
                                </tr></thead>
                                <tbody>
                                    {Object.entries(data.by_type).map(([k, v]) => (
                                        <tr key={k} className="border-b border-ink/5">
                                            <td className="px-4 py-2 capitalize">{k}</td>
                                            <td className="px-4 py-2 text-right tabular-nums">{v.count}</td>
                                            <td className="px-4 py-2 text-right tabular-nums">{fmt(v.total_amount)}</td>
                                            <td className="px-4 py-2 text-right tabular-nums">{fmt(v.total_balance)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="bg-white border border-ink/10">
                            <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg">By status</div>
                            <table className="w-full text-sm">
                                <thead><tr className="border-b border-ink/10 bg-bone/40">
                                    <th className="text-left overline px-4 py-2">Status</th>
                                    <th className="text-right overline px-4 py-2">Count</th>
                                    <th className="text-right overline px-4 py-2">Amount</th>
                                    <th className="text-right overline px-4 py-2">Balance</th>
                                </tr></thead>
                                <tbody>
                                    {Object.entries(data.by_status).map(([k, v]) => (
                                        <tr key={k} className="border-b border-ink/5">
                                            <td className="px-4 py-2 capitalize">{k}</td>
                                            <td className="px-4 py-2 text-right tabular-nums">{v.count}</td>
                                            <td className="px-4 py-2 text-right tabular-nums">{fmt(v.total_amount)}</td>
                                            <td className="px-4 py-2 text-right tabular-nums">{fmt(v.total_balance)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {data.by_department.length > 0 && (
                        <div className="bg-white border border-ink/10">
                            <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg">By department</div>
                            <table className="w-full text-sm">
                                <thead><tr className="border-b border-ink/10 bg-bone/40">
                                    <th className="text-left overline px-4 py-2">Department</th>
                                    <th className="text-right overline px-4 py-2">Records</th>
                                    <th className="text-right overline px-4 py-2">Disbursed</th>
                                    <th className="text-right overline px-4 py-2">Deducted</th>
                                    <th className="text-right overline px-4 py-2">Outstanding</th>
                                </tr></thead>
                                <tbody>
                                    {data.by_department.map((c) => (
                                        <tr key={c.department} className="border-b border-ink/5">
                                            <td className="px-4 py-2 text-ink font-medium">{c.department}</td>
                                            <td className="px-4 py-2 text-right tabular-nums">{c.count}</td>
                                            <td className="px-4 py-2 text-right tabular-nums">{fmt(c.total_amount)}</td>
                                            <td className="px-4 py-2 text-right tabular-nums text-green-700">{fmt(c.total_deducted)}</td>
                                            <td className="px-4 py-2 text-right tabular-nums text-amber-700">{fmt(c.total_balance)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="bg-white border border-ink/10 overflow-x-auto" data-testid="loans-report-rows">
                        <table className="w-full text-sm border-collapse">
                            <thead><tr className="border-b-2 border-ink/20 bg-bone">
                                <th className="text-left overline px-3 py-3">Type</th>
                                <th className="text-left overline px-3 py-3">Employee</th>
                                <th className="text-left overline px-3 py-3 hidden lg:table-cell">Dept · Co</th>
                                <th className="text-right overline px-3 py-3">Amount</th>
                                <th className="text-right overline px-3 py-3">Deducted</th>
                                <th className="text-right overline px-3 py-3">Balance</th>
                                <th className="text-right overline px-3 py-3 hidden md:table-cell">Tenure</th>
                                <th className="text-left overline px-3 py-3">Status</th>
                            </tr></thead>
                            <tbody>
                                {data.rows.map((r) => (
                                    <tr key={r.id} className="border-b border-ink/5">
                                        <td className="px-3 py-2">
                                            <span className={`inline-flex items-center gap-1 text-xs ${r.type === "advance" ? "text-amber-700" : "text-klein"}`}>
                                                {r.type === "advance" ? "Advance" : "Loan"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="text-ink">{r.employee_name}</div>
                                            <div className="text-xs text-ink/50">{r.employee_no || "—"}</div>
                                        </td>
                                        <td className="px-3 py-2 text-ink/70 hidden lg:table-cell text-xs">
                                            {r.department || "—"}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">{fmt(r.amount)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-green-700">{fmt(r.deducted)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-amber-700">{fmt(r.balance)}</td>
                                        <td className="px-3 py-2 text-right text-xs hidden md:table-cell">
                                            {r.type === "advance" ? "Single" : `${r.paid_installments}/${r.tenure_months}`}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="text-xs uppercase tracking-[0.1em] text-ink/70">{r.status}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function AttendanceReport() {
    const today = new Date().toISOString().slice(0, 10);
    const monthStart = today.slice(0, 8) + "01";
    const [mode, setMode] = useState("daily"); // "daily" | "monthly" | "range"
    const [dailyDate, setDailyDate] = useState(today);
    const [monthlyPeriod, setMonthlyPeriod] = useState(today.slice(0, 7));
    const [rangeStart, setRangeStart] = useState(monthStart);
    const [rangeEnd, setRangeEnd] = useState(today);
    const [employeeId, setEmployeeId] = useState("");     // "" = all
    const [employees, setEmployees] = useState([]);
    const [data, setData] = useState(null);
    const [genParams, setGenParams] = useState(null);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/employees", { params: { status: "active", page_size: 5000 } });
                const arr = Array.isArray(data) ? data : (data.rows || data.items || []);
                setEmployees(arr);
            } catch { /* silent */ }
        })();
    }, []);

    const params = useMemo(() => {
        if (mode === "daily") return { date: dailyDate, employee_id: employeeId || undefined };
        if (mode === "monthly") return { period: monthlyPeriod, employee_id: employeeId || undefined };
        return { start: rangeStart, end: rangeEnd, employee_id: employeeId || undefined };
    }, [mode, dailyDate, monthlyPeriod, rangeStart, rangeEnd, employeeId]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/attendance-log", { params });
            setData(data);
            setGenParams(params);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not generate attendance report");
            setData(null);
        } finally { setLoading(false); }
    };

    const onExport = async (path = "attendance-log.xlsx", prefix = "attendance_log") => {
        try {
            const p = genParams || params;
            const res = await api.get(`/reports/${path}`, {
                params: p,
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            const label = p.date || p.period || `${p.start}_to_${p.end}`;
            a.download = `${prefix}_${label}.${path.split(".").pop()}`;
            document.body.appendChild(a); a.click();
            a.remove(); URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Export failed");
        }
    };

    const filteredRows = useMemo(() => {
        if (!data) return [];
        const term = q.trim().toLowerCase();
        return data.rows.filter((r) => {
            if (statusFilter !== "all" && r.lateness !== statusFilter) return false;
            if (!term) return true;
            return (
                (r.employee_name || "").toLowerCase().includes(term) ||
                (r.employee_no || "").toLowerCase().includes(term) ||
                (r.department || "").toLowerCase().includes(term)
            );
        });
    }, [data, q, statusFilter]);

    const statusBadgeCls = (s) => {
        switch (s) {
            case "On Time": return "bg-green-100 text-green-800";
            case "Late":
            case "Half Day": return "bg-amber-100 text-amber-900";
            case "Absent":
            case "No Record": return "bg-red-100 text-red-800";
            case "On Leave": return "bg-blue-100 text-blue-800";
            case "Holiday": return "bg-purple-100 text-purple-800";
            case "Weekend": return "bg-ink/5 text-ink/60";
            default: return "bg-ink/5 text-ink/60";
        }
    };

    return (
        <div className="space-y-6">
            {/* Filter bar */}
            <div className="bg-white border border-ink/10 p-5 space-y-4" data-testid="attendance-log-filters">
                <div className="flex flex-wrap items-end gap-4">
                    {/* Mode toggle */}
                    <div className="flex items-center gap-1 border border-ink/10 p-1" role="tablist">
                        {[
                            { k: "daily", label: "Daily" },
                            { k: "monthly", label: "Monthly" },
                            { k: "range", label: "Range" },
                        ].map((t) => (
                            <button
                                key={t.k}
                                onClick={() => setMode(t.k)}
                                className={`text-xs px-3 py-1.5 font-medium ${mode === t.k ? "bg-klein text-white" : "text-ink/60 hover:bg-bone"}`}
                                data-testid={`attendance-log-mode-${t.k}`}
                            >{t.label}</button>
                        ))}
                    </div>

                    {/* Date input(s) */}
                    {mode === "daily" && (
                        <div>
                            <div className="overline mb-1.5">Date</div>
                            <input type="date" value={dailyDate} onChange={(e) => setDailyDate(e.target.value)}
                                className="text-sm border border-ink/15 px-3 py-1.5 focus:outline-none focus:border-klein"
                                data-testid="attendance-log-daily-date" />
                        </div>
                    )}
                    {mode === "monthly" && (
                        <div>
                            <div className="overline mb-1.5">Month</div>
                            <input type="month" value={monthlyPeriod} onChange={(e) => setMonthlyPeriod(e.target.value)}
                                className="text-sm border border-ink/15 px-3 py-1.5 focus:outline-none focus:border-klein"
                                data-testid="attendance-log-monthly-period" />
                        </div>
                    )}
                    {mode === "range" && (
                        <>
                            <div>
                                <div className="overline mb-1.5">From</div>
                                <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)}
                                    className="text-sm border border-ink/15 px-3 py-1.5 focus:outline-none focus:border-klein"
                                    data-testid="attendance-log-range-start" />
                            </div>
                            <div>
                                <div className="overline mb-1.5">To</div>
                                <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)}
                                    className="text-sm border border-ink/15 px-3 py-1.5 focus:outline-none focus:border-klein"
                                    data-testid="attendance-log-range-end" />
                            </div>
                        </>
                    )}

                    {/* Employee filter */}
                    <div className="min-w-[220px] flex-1">
                        <div className="overline mb-1.5">Employee</div>
                        <EmployeePicker employees={employees} value={employeeId} onChange={setEmployeeId} allLabel="All employees" className="py-1.5" testid="attendance-log-employee" />
                    </div>

                    <div className="flex items-center gap-2 ml-auto">
                        <button onClick={load} disabled={loading}
                            className="inline-flex items-center gap-2 bg-klein text-white px-4 py-2 text-xs font-medium hover:bg-klein/90 disabled:opacity-60"
                            data-testid="attendance-log-generate">
                            {loading ? "Loading…" : "Generate"}
                        </button>
                        {data && (
                            <>
                                <button onClick={() => onExport()}
                                    className="inline-flex items-center gap-2 bg-ink text-white px-4 py-2 text-xs font-medium hover:bg-ink/90"
                                    data-testid="attendance-log-export">
                                    <Download className="w-3.5 h-3.5" /> Export Excel
                                </button>
                                <button onClick={() => onExport("lateness.xlsx", "lateness")}
                                    className="inline-flex items-center gap-2 border border-amber-600/40 text-amber-800 px-4 py-2 text-xs font-medium hover:bg-amber-50"
                                    data-testid="lateness-export-xlsx">
                                    <Download className="w-3.5 h-3.5" /> Lateness Excel
                                </button>
                                <button onClick={() => onExport("lateness.pdf", "lateness")}
                                    className="inline-flex items-center gap-2 border border-amber-600/40 text-amber-800 px-4 py-2 text-xs font-medium hover:bg-amber-50"
                                    data-testid="lateness-export-pdf">
                                    <Download className="w-3.5 h-3.5" /> Lateness PDF
                                </button>
                            </>
                        )}
                    </div>
                </div>
                {data && (
                    <div className="text-xs text-ink/60 border-t border-ink/10 pt-3">
                        Range <b>{data.start}</b> → <b>{data.end}</b> · Late after <b>{data.late_threshold}</b> (work start {data.work_start_time} + {data.grace_minutes}m grace)
                    </div>
                )}
            </div>

            {data && (
                <div className="space-y-5" data-testid="attendance-log-result">
                    {/* KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-ink/10">
                        <div className="bg-white p-4"><div className="overline">Rows</div><div className="font-display font-black text-2xl mt-1 tabular-nums">{data.rows.length}</div></div>
                        <div className="bg-white p-4"><div className="overline">Records</div><div className="font-display font-black text-2xl mt-1 tabular-nums">{data.totals.records}</div></div>
                        <div className="bg-white p-4"><div className="overline">On Time</div><div className="font-display font-black text-2xl mt-1 tabular-nums text-green-700">{data.totals.on_time}</div></div>
                        <div className="bg-white p-4"><div className="overline">Late</div><div className="font-display font-black text-2xl mt-1 tabular-nums text-amber-800">{data.totals.late}</div></div>
                        <div className="bg-white p-4"><div className="overline">Absent</div><div className="font-display font-black text-2xl mt-1 tabular-nums text-red-700">{data.totals.absent}</div></div>
                        <div className="bg-white p-4"><div className="overline">Hours</div><div className="font-display font-black text-2xl mt-1 tabular-nums">{data.totals.hours}</div></div>
                    </div>

                    {/* Secondary filters */}
                    <div className="flex flex-wrap items-center gap-3">
                        <input placeholder="Search name, no, dept…" value={q} onChange={(e) => setQ(e.target.value)}
                            className="text-sm border border-ink/15 px-3 py-1.5 focus:outline-none focus:border-klein min-w-[220px]"
                            data-testid="attendance-log-search" />
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                            className="text-sm border border-ink/15 px-3 py-1.5 focus:outline-none focus:border-klein bg-white"
                            data-testid="attendance-log-status">
                            <option value="all">All statuses</option>
                            <option value="On Time">On Time</option>
                            <option value="Late">Late</option>
                            <option value="Absent">Absent</option>
                            <option value="No Record">No Record</option>
                            <option value="Half Day">Half Day</option>
                            <option value="On Leave">On Leave</option>
                            <option value="Holiday">Holiday</option>
                            <option value="Weekend">Weekend</option>
                        </select>
                        <div className="text-xs text-ink/50 ml-auto">Showing {filteredRows.length} of {data.rows.length} rows</div>
                    </div>

                    {/* Log table */}
                    {filteredRows.length > 0 ? (
                        <div className="bg-white border border-ink/10 overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                                <thead className="sticky top-0 z-10">
                                    <tr className="border-b-2 border-ink/20 bg-bone">
                                        <th className="text-left overline px-3 py-3">Employee</th>
                                        <th className="text-left overline px-3 py-3 hidden md:table-cell">Dept</th>
                                        <th className="text-left overline px-3 py-3">Date</th>
                                        <th className="text-right overline px-3 py-3">Clock-In</th>
                                        <th className="text-right overline px-3 py-3">Clock-Out</th>
                                        <th className="text-right overline px-3 py-3">Hours</th>
                                        <th className="text-left overline px-3 py-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRows.map((r, i) => (
                                        <tr key={`${r.employee_id}-${r.date}-${i}`} className="border-b border-ink/5 hover:bg-bone/40"
                                            data-testid="attendance-log-row">
                                            <td className="px-3 py-2">
                                                <div className="text-ink">{r.employee_name}</div>
                                                <div className="text-xs text-ink/50">{r.employee_no || "—"}</div>
                                            </td>
                                            <td className="px-3 py-2 text-ink/70 text-xs hidden md:table-cell">{r.department || "—"}</td>
                                            <td className="px-3 py-2 text-ink/80 text-xs tabular-nums">
                                                {r.date} <span className="text-ink/40">· {r.weekday}</span>
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums">{r.check_in || "—"}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{r.check_out || "—"}</td>
                                            <td className="px-3 py-2 text-right tabular-nums font-medium">{r.hours || "—"}</td>
                                            <td className="px-3 py-2">
                                                <span className={`inline-flex text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 ${statusBadgeCls(r.lateness)}`}>
                                                    {r.lateness}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="bg-white border border-ink/10 px-6 py-12 text-center text-ink/40">
                            No attendance data matches the current filters.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


function SettlementsReport() {
    const today = new Date().toISOString().slice(0, 10);
    const firstOfYear = today.slice(0, 4) + "-01-01";
    const [filters, setFilters] = useState({ start: firstOfYear, end: today, kind: "all", status: "all" });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/reports/settlements", { params: filters });
            setData(d);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not load settlement report");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    const downloadPdf = async (id) => {
        try {
            const res = await api.get(`/settlements/${id}/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url; a.download = `settlement_${id}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error("PDF download failed");
        }
    };

    return (
        <div data-testid="settlements-report">
            <div className="bg-white border border-ink/10 p-5 grid grid-cols-2 md:grid-cols-5 gap-4 items-end">
                <div>
                    <label className="overline block mb-2">From</label>
                    <input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="w-full border border-ink/15 px-3 py-2.5 text-sm" data-testid="ss-start" />
                </div>
                <div>
                    <label className="overline block mb-2">To</label>
                    <input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="w-full border border-ink/15 px-3 py-2.5 text-sm" data-testid="ss-end" />
                </div>
                <div>
                    <label className="overline block mb-2">Type</label>
                    <select value={filters.kind} onChange={(e) => setFilters({ ...filters, kind: e.target.value })} className="w-full border border-ink/15 px-3 py-2.5 text-sm bg-transparent" data-testid="ss-kind">
                        <option value="all">All</option>
                        <option value="resignation">Resignation</option>
                        <option value="termination">Termination</option>
                    </select>
                </div>
                <div>
                    <label className="overline block mb-2">Stage</label>
                    <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full border border-ink/15 px-3 py-2.5 text-sm bg-transparent" data-testid="ss-status">
                        <option value="all">All</option>
                        <option value="pending_dept_approval">Pending dept approval</option>
                        <option value="pending_finance_approval">Pending finance approval</option>
                        <option value="pending_disbursement">Pending disbursement</option>
                        <option value="disbursed">Disbursed</option>
                        <option value="rejected">Rejected</option>
                    </select>
                </div>
                <button onClick={load} disabled={loading} className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="ss-generate">
                    {loading ? "Loading…" : "Generate"}
                </button>
            </div>

            {data && (
                <>
                    <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
                        {[
                            { label: "Cases", value: data.totals.count, accent: false },
                            { label: "Gross payable", value: fmt(data.totals.gross_payable), accent: false },
                            { label: "Total deductions", value: fmt(data.totals.deductions), accent: false },
                            { label: "Net disbursed/owed", value: fmt(data.totals.net_payable), accent: true },
                        ].map((s) => (
                            <div key={s.label} className={`p-5 ${s.accent ? "bg-klein text-white" : "bg-white"}`}>
                                <div className={`overline ${s.accent ? "!text-white/70" : ""}`}>{s.label}</div>
                                <div className="mt-2 font-display font-bold text-2xl tabular-nums">{s.value}</div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 bg-white border border-ink/10 overflow-x-auto max-h-[420px]">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b-2 border-ink/20 bg-bone">
                                    <th className="text-left overline px-4 py-3">Employee</th>
                                    <th className="text-left overline px-4 py-3">Type</th>
                                    <th className="text-left overline px-4 py-3 hidden md:table-cell">Effective</th>
                                    <th className="text-right overline px-4 py-3">Gross payable</th>
                                    <th className="text-right overline px-4 py-3">Deductions</th>
                                    <th className="text-right overline px-4 py-3">Net</th>
                                    <th className="text-left overline px-4 py-3">Stage</th>
                                    <th className="text-right overline px-4 py-3">PDF</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.rows.length === 0 ? (
                                    <tr><td colSpan="8" className="px-6 py-16 text-center text-ink/40">No settlements in this range.</td></tr>
                                ) : data.rows.map((r) => (
                                    <tr key={r.id} className="border-b border-ink/10" data-testid={`settlements-report-row-${r.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="text-ink font-medium">{r.employee_name}</div>
                                            <div className="text-xs text-ink/50">{r.department || "—"}</div>
                                        </td>
                                        <td className="px-4 py-3 capitalize">{r.kind}</td>
                                        <td className="px-4 py-3 hidden md:table-cell">{r.end_date}</td>
                                        <td className="px-4 py-3 text-right tabular-nums">{fmt(r.gross_payable)}</td>
                                        <td className="px-4 py-3 text-right tabular-nums">{fmt(r.deductions)}</td>
                                        <td className="px-4 py-3 text-right tabular-nums font-bold text-klein">{fmt(r.net_payable)}</td>
                                        <td className="px-4 py-3">
                                            <span className="inline-flex items-center px-2 py-1 border text-[10px] uppercase tracking-[0.15em] bg-ink/5 border-ink/10 text-ink/70">
                                                {(r.status || "").replace(/_/g, " ")}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => downloadPdf(r.id)} className="p-2 text-ink/50 hover:text-klein" title="Download PDF">
                                                <Download className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

function PerformanceReport() {
    const [filters, setFilters] = useState({ period: "", department: "", status: "finalized" });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/reports/performance", { params: filters });
            setData(d);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not load performance report");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid="performance-report">
            <div className="bg-white border border-ink/10 p-5 grid grid-cols-2 md:grid-cols-5 gap-4 items-end">
                <div>
                    <label className="overline block mb-2">Period</label>
                    <input type="text" value={filters.period} onChange={(e) => setFilters({ ...filters, period: e.target.value })} placeholder="e.g. 2026-H1" className="w-full border border-ink/15 px-3 py-2.5 text-sm" data-testid="pr-period" />
                </div>
                <div>
                    <label className="overline block mb-2">Department</label>
                    <input type="text" value={filters.department} onChange={(e) => setFilters({ ...filters, department: e.target.value })} className="w-full border border-ink/15 px-3 py-2.5 text-sm" data-testid="pr-department" />
                </div>
                <div>
                    <label className="overline block mb-2">Stage</label>
                    <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full border border-ink/15 px-3 py-2.5 text-sm bg-transparent" data-testid="pr-status">
                        <option value="finalized">Finalized</option>
                        <option value="dept_approved">Pending HR</option>
                        <option value="submitted">Pending Dept</option>
                        <option value="all">All</option>
                    </select>
                </div>
                <button onClick={load} disabled={loading} className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="pr-generate">
                    {loading ? "Loading…" : "Generate"}
                </button>
            </div>

            {data && (
                <>
                    <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
                        <div className="bg-white p-5">
                            <div className="overline">Reviews</div>
                            <div className="mt-2 font-display font-bold text-2xl tabular-nums">{data.totals.count}</div>
                        </div>
                        <div className="bg-white p-5">
                            <div className="overline">Average score</div>
                            <div className="mt-2 font-display font-bold text-2xl tabular-nums">{data.totals.average_score}<span className="text-sm text-ink/40">/100</span></div>
                        </div>
                        <div className="bg-klein text-white p-5">
                            <div className="overline !text-white/70">Outstanding</div>
                            <div className="mt-2 font-display font-bold text-2xl tabular-nums">{data.by_rating?.["Outstanding"] || 0}</div>
                        </div>
                    </div>

                    {data.by_department?.length > 0 && (
                        <div className="mt-6 bg-white border border-ink/10 p-5">
                            <div className="overline mb-3">Average by department</div>
                            <div className="space-y-2">
                                {data.by_department.sort((a, b) => b.avg - a.avg).map((d) => (
                                    <div key={d.department} className="flex items-center gap-4">
                                        <div className="w-40 text-sm text-ink truncate">{d.department}</div>
                                        <div className="flex-1 h-2 bg-ink/5">
                                            <div className="h-full bg-klein" style={{ width: `${Math.min(100, d.avg)}%` }} />
                                        </div>
                                        <div className="w-20 text-right text-sm tabular-nums font-bold text-ink">{d.avg.toFixed(1)}</div>
                                        <div className="w-12 text-right text-xs text-ink/40 tabular-nums">{d.count}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-6 bg-white border border-ink/10 overflow-x-auto max-h-[460px]">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b-2 border-ink/20 bg-bone">
                                    <th className="text-right overline px-4 py-3 w-[60px]">Rank</th>
                                    <th className="text-left overline px-4 py-3">Employee</th>
                                    <th className="text-left overline px-4 py-3 hidden md:table-cell">Department</th>
                                    <th className="text-left overline px-4 py-3 hidden md:table-cell">Period</th>
                                    <th className="text-right overline px-4 py-3">Score</th>
                                    <th className="text-left overline px-4 py-3">Rating</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data.rows || []).length === 0 ? (
                                    <tr><td colSpan="6" className="px-6 py-16 text-center text-ink/40">No reviews match these filters.</td></tr>
                                ) : (data.rows || []).map((r) => (
                                    <tr key={r.id} className="border-b border-ink/10" data-testid={`pr-row-${r.id}`}>
                                        <td className="px-4 py-3 text-right text-ink/40 font-display font-bold tabular-nums">{r.rank}</td>
                                        <td className="px-4 py-3">
                                            <div className="text-ink font-medium">{r.employee_name}</div>
                                            <div className="text-xs text-ink/50">{r.position || "—"}</div>
                                        </td>
                                        <td className="px-4 py-3 hidden md:table-cell">{r.department || "—"}</td>
                                        <td className="px-4 py-3 hidden md:table-cell">{r.period}</td>
                                        <td className="px-4 py-3 text-right tabular-nums">
                                            <span className="font-display font-bold text-lg text-klein">{Number(r.normalized_score).toFixed(1)}</span>
                                            <span className="text-xs text-ink/40">/100</span>
                                        </td>
                                        <td className={`px-4 py-3 font-medium ${r.rating === "Outstanding" ? "text-green-700" : r.rating === "Below expectations" ? "text-destructive" : "text-ink/70"}`}>{r.rating}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}



function LeavesReport() {
    const today = new Date();
    const firstOfYear = new Date(today.getFullYear(), 0, 1);
    const [start, setStart] = useState(firstOfYear.toISOString().slice(0, 10));
    const [end, setEnd] = useState(today.toISOString().slice(0, 10));
    const [status, setStatus] = useState("all");
    const [leaveType, setLeaveType] = useState("all");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { start, end };
            if (status !== "all") params.status = status;
            if (leaveType !== "all") params.leave_type = leaveType;
            const { data } = await api.get("/reports/leaves", { params });
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not generate leave report");
            setData(null);
        } finally { setLoading(false); }
    }, [start, end, status, leaveType]);

    useEffect(() => { load(); }, [load]);

    const onExport = () => {
        if (!data) return;
        const rows = data.rows.map((r) => [
            r.employee_no, r.employee_name, r.department, r.leave_type, r.status,
            r.start_date, r.end_date, r.days, r.reason, r.approved_by, r.approved_at, r.created_at,
        ]);
        exportCSV(
            `leaves_${start}_${end}.csv`,
            ["Emp No", "Name", "Dept", "Leave Type", "Status", "Start", "End", "Days", "Reason", "Approved By", "Approved At", "Created"],
            rows,
        );
    };

    const statusColor = (s) => s === "approved" ? "text-emerald-700 bg-emerald-50" :
        s === "rejected" ? "text-red-700 bg-red-50" :
        s === "cancelled" ? "text-ink/50 bg-ink/5" : "text-amber-700 bg-amber-50";

    return (
        <div className="space-y-6">
            <div className="bg-white border border-ink/10 p-5 flex items-end gap-4 flex-wrap" data-testid="leaves-report-filters">
                <div>
                    <label className="overline block mb-2">Start</label>
                    <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none" data-testid="leaves-start" />
                </div>
                <div>
                    <label className="overline block mb-2">End</label>
                    <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none" data-testid="leaves-end" />
                </div>
                <div>
                    <label className="overline block mb-2">Status</label>
                    <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-ink/15 px-3 py-2.5 text-sm bg-transparent focus:border-klein focus:outline-none" data-testid="leaves-status">
                        <option value="all">All</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                </div>
                <div>
                    <label className="overline block mb-2">Type</label>
                    <select value={leaveType} onChange={(e) => setLeaveType(e.target.value)} className="border border-ink/15 px-3 py-2.5 text-sm bg-transparent focus:border-klein focus:outline-none" data-testid="leaves-type">
                        <option value="all">All</option>
                        <option value="annual">Annual</option>
                        <option value="sick">Sick</option>
                        <option value="maternity">Maternity</option>
                        <option value="paternity">Paternity</option>
                        <option value="unpaid">Unpaid</option>
                        <option value="compassionate">Compassionate</option>
                    </select>
                </div>
                <button onClick={load} disabled={loading} className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="leaves-load-button">
                    {loading ? "Loading…" : "Generate"}
                </button>
                {data && (
                    <button onClick={onExport} className="ml-auto inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5" data-testid="leaves-export-button">
                        <Download className="w-4 h-4" /> Export CSV
                    </button>
                )}
            </div>

            {data && (
                <div className="space-y-6" data-testid="leaves-report-result">
                    {/* Totals strip */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10">
                        <div className="bg-white p-5">
                            <div className="overline">Total requests</div>
                            <div className="font-display font-black text-3xl mt-2 tabular-nums">{data.totals.total_requests}</div>
                        </div>
                        <div className="bg-white p-5">
                            <div className="overline">Approved days</div>
                            <div className="font-display font-black text-3xl mt-2 tabular-nums text-emerald-700">{data.totals.approved_days}</div>
                        </div>
                        <div className="bg-white p-5">
                            <div className="overline">Pending days</div>
                            <div className="font-display font-black text-3xl mt-2 tabular-nums text-amber-700">{data.totals.pending_days}</div>
                        </div>
                        <div className="bg-white p-5">
                            <div className="overline">Unique employees</div>
                            <div className="font-display font-black text-3xl mt-2 tabular-nums text-klein">{data.totals.unique_employees}</div>
                        </div>
                    </div>

                    {/* By status + by type */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white border border-ink/10" data-testid="leaves-by-status">
                            <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg">By status</div>
                            <table className="w-full text-sm">
                                <tbody>
                                    {Object.entries(data.by_status).map(([k, v]) => (
                                        <tr key={k} className="border-b border-ink/5">
                                            <td className="px-4 py-2.5 capitalize">
                                                <span className={`inline-block px-2 py-0.5 text-xs rounded ${statusColor(k)}`}>{k}</span>
                                            </td>
                                            <td className="px-4 py-2.5 text-right tabular-nums font-medium">{v}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="bg-white border border-ink/10" data-testid="leaves-by-type">
                            <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg">By leave type (approved days)</div>
                            <table className="w-full text-sm">
                                <tbody>
                                    {data.by_type.length === 0 && <tr><td className="px-4 py-3 text-ink/40 text-xs">No approved leaves in this period</td></tr>}
                                    {data.by_type.map((r) => (
                                        <tr key={r.type} className="border-b border-ink/5">
                                            <td className="px-4 py-2.5 capitalize text-ink/80">{r.type}</td>
                                            <td className="px-4 py-2.5 text-right tabular-nums font-medium">{r.days}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* By department */}
                    {data.by_department.length > 0 && (
                        <div className="bg-white border border-ink/10" data-testid="leaves-by-department">
                            <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg">By department (approved days)</div>
                            <table className="w-full text-sm">
                                <thead><tr className="border-b border-ink/10 bg-bone/40">
                                    <th className="text-left overline px-4 py-2">Department</th>
                                    <th className="text-right overline px-4 py-2">Days</th>
                                </tr></thead>
                                <tbody>
                                    {data.by_department.map((r) => (
                                        <tr key={r.department} className="border-b border-ink/5">
                                            <td className="px-4 py-2.5 text-ink">{r.department || "—"}</td>
                                            <td className="px-4 py-2.5 text-right tabular-nums font-medium">{r.days}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Top consumers */}
                    {data.top_consumers.length > 0 && (
                        <div className="bg-white border border-ink/10" data-testid="leaves-top-consumers">
                            <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg">
                                Top leave consumers <span className="text-ink/40 text-sm font-normal">· top {data.top_consumers.length}</span>
                            </div>
                            <table className="w-full text-sm">
                                <thead><tr className="border-b border-ink/10 bg-bone/40">
                                    <th className="text-left overline px-4 py-2">Emp No</th>
                                    <th className="text-left overline px-4 py-2">Name</th>
                                    <th className="text-left overline px-4 py-2">Dept</th>
                                    <th className="text-right overline px-4 py-2">Approved Requests</th>
                                    <th className="text-right overline px-4 py-2">Approved Days</th>
                                </tr></thead>
                                <tbody>
                                    {data.top_consumers.map((r, i) => (
                                        <tr key={r.employee_id || r.employee_no || `${r.name}-${i}`} className="border-b border-ink/5">
                                            <td className="px-4 py-2.5 text-ink/70">{r.employee_no || "—"}</td>
                                            <td className="px-4 py-2.5 text-ink">{r.name}</td>
                                            <td className="px-4 py-2.5 text-ink/70">{r.department || "—"}</td>
                                            <td className="px-4 py-2.5 text-right tabular-nums">{r.approved_count}</td>
                                            <td className="px-4 py-2.5 text-right tabular-nums font-medium text-klein">{r.approved_days}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Detailed rows */}
                    <div className="bg-white border border-ink/10 overflow-x-auto" data-testid="leaves-detailed-rows">
                        <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg">
                            Detailed requests <span className="text-ink/40 text-sm font-normal">· {data.rows.length}</span>
                        </div>
                        <table className="w-full text-sm border-collapse">
                            <thead><tr className="border-b-2 border-ink/20 bg-bone">
                                <th className="text-left overline px-3 py-3">Employee</th>
                                <th className="text-left overline px-3 py-3 hidden md:table-cell">Dept</th>
                                <th className="text-left overline px-3 py-3">Type</th>
                                <th className="text-left overline px-3 py-3">Start</th>
                                <th className="text-left overline px-3 py-3 hidden lg:table-cell">End</th>
                                <th className="text-right overline px-3 py-3">Days</th>
                                <th className="text-left overline px-3 py-3">Status</th>
                                <th className="text-left overline px-3 py-3 hidden xl:table-cell">Reason</th>
                            </tr></thead>
                            <tbody>
                                {data.rows.length === 0 && <tr><td colSpan="8" className="px-4 py-6 text-center text-sm text-ink/40">No leave requests in this period</td></tr>}
                                {data.rows.map((r) => (
                                    <tr key={r.id} className="border-b border-ink/5">
                                        <td className="px-3 py-2">
                                            <div className="text-ink">{r.employee_name}</div>
                                            <div className="text-xs text-ink/50">{r.employee_no || "—"}</div>
                                        </td>
                                        <td className="px-3 py-2 text-ink/70 hidden md:table-cell">{r.department || "—"}</td>
                                        <td className="px-3 py-2 capitalize text-ink/80">{r.leave_type}</td>
                                        <td className="px-3 py-2 text-ink/70 tabular-nums">{r.start_date || "—"}</td>
                                        <td className="px-3 py-2 text-ink/70 tabular-nums hidden lg:table-cell">{r.end_date || "—"}</td>
                                        <td className="px-3 py-2 text-right tabular-nums font-medium">{r.days}</td>
                                        <td className="px-3 py-2">
                                            <span className={`inline-block px-2 py-0.5 text-xs rounded capitalize ${statusColor(r.status)}`}>{r.status}</span>
                                        </td>
                                        <td className="px-3 py-2 text-xs text-ink/60 hidden xl:table-cell max-w-xs truncate" title={r.reason}>{r.reason || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatutoryReport() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/statutory-contributions");
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not generate report");
            setData(null);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onExport = () => {
        if (!data) return;
        const rows = data.rows.map((r) => [
            r.employee_no, r.name, r.department, r.category, r.monthly_gross, r.itf_1pct, r.nsitf_1pct,
        ]);
        exportCSV(
            "itf_nsitf_contributions.csv",
            ["Emp No", "Name", "Department", "Category", "Monthly Gross", "ITF 1%", "NSITF 1%"],
            rows,
        );
    };

    if (loading && !data) return <div className="py-12 text-ink/40" data-testid="statutory-loading">Calculating…</div>;
    if (!data) return null;

    return (
        <div className="space-y-6" data-testid="statutory-report">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <p className="text-sm text-ink/60 max-w-2xl">
                    <strong>ITF</strong> (Industrial Training Fund) and <strong>NSITF</strong> (Nigerian Social Insurance Trust Fund) — both calculated at <strong>1% of monthly gross emoluments</strong> paid by the employer.
                </p>
                <button onClick={onExport} className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5" data-testid="statutory-export-btn">
                    <Download className="w-4 h-4" /> Export CSV
                </button>
            </div>

            {/* Eligibility banner */}
            <div className={`p-4 rounded border-2 ${data.itf_eligible ? "bg-amber-50 border-amber-300" : "bg-bone/40 border-ink/15"}`} data-testid="statutory-eligibility">
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-ink/60">ITF Eligibility</div>
                <p className="mt-1 text-sm text-ink">
                    {data.itf_eligible ? (
                        <>
                            <span className="font-semibold text-amber-700">ITF Levy required.</span> {data.itf_eligibility_reason}.
                            All Nigerian employers with 25+ employees or ₦50M+ annual gross emoluments must remit 1% to ITF.
                        </>
                    ) : (
                        <>Not yet ITF-eligible — {data.itf_eligibility_reason}. NSITF still applies to all employers.</>
                    )}
                </p>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10" data-testid="statutory-kpis">
                <div className="bg-white p-5">
                    <div className="overline">Headcount</div>
                    <div className="font-display font-black text-3xl mt-2 tabular-nums">{data.headcount}</div>
                </div>
                <div className="bg-white p-5">
                    <div className="overline">Total monthly gross</div>
                    <div className="font-display font-black text-3xl mt-2 tabular-nums">{fmt(data.total_monthly_gross)}</div>
                </div>
                <div className="bg-white p-5">
                    <div className="overline">ITF (monthly)</div>
                    <div className="font-display font-black text-3xl mt-2 tabular-nums text-amber-700">{fmt(data.monthly_itf_1pct)}</div>
                    <div className="text-[11px] text-ink/40 mt-1">Annual: {fmt(data.annual_itf_1pct)}</div>
                </div>
                <div className="bg-white p-5">
                    <div className="overline">NSITF (monthly)</div>
                    <div className="font-display font-black text-3xl mt-2 tabular-nums text-klein">{fmt(data.monthly_nsitf_1pct)}</div>
                    <div className="text-[11px] text-ink/40 mt-1">Annual: {fmt(data.annual_nsitf_1pct)}</div>
                </div>
            </div>

            {/* By department */}
            {data.by_department.length > 0 && (
                <div className="bg-white border border-ink/10" data-testid="statutory-by-dept">
                    <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg">By department</div>
                    <table className="w-full text-sm">
                        <thead><tr className="border-b border-ink/10 bg-bone/40">
                            <th className="text-left overline px-4 py-2">Department</th>
                            <th className="text-right overline px-4 py-2">Monthly gross</th>
                            <th className="text-right overline px-4 py-2">ITF 1%</th>
                            <th className="text-right overline px-4 py-2">NSITF 1%</th>
                        </tr></thead>
                        <tbody>
                            {data.by_department.map((d) => (
                                <tr key={d.department} className="border-b border-ink/5">
                                    <td className="px-4 py-2.5 text-ink">{d.department}</td>
                                    <td className="px-4 py-2.5 text-right tabular-nums">{fmt(d.monthly_gross)}</td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-amber-700">{fmt(d.itf_1pct)}</td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-klein">{fmt(d.nsitf_1pct)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Per-employee detail */}
            <div className="bg-white border border-ink/10 overflow-x-auto" data-testid="statutory-rows">
                <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg">
                    Per-employee breakdown <span className="text-ink/40 text-sm font-normal">· {data.rows.length}</span>
                </div>
                <table className="w-full text-sm border-collapse">
                    <thead><tr className="border-b-2 border-ink/20 bg-bone">
                        <th className="text-left overline px-3 py-3">Emp No</th>
                        <th className="text-left overline px-3 py-3">Name</th>
                        <th className="text-left overline px-3 py-3 hidden md:table-cell">Department</th>
                        <th className="text-left overline px-3 py-3 hidden lg:table-cell">Category</th>
                        <th className="text-right overline px-3 py-3">Monthly gross</th>
                        <th className="text-right overline px-3 py-3">ITF 1%</th>
                        <th className="text-right overline px-3 py-3">NSITF 1%</th>
                    </tr></thead>
                    <tbody>
                        {data.rows.length === 0 && <tr><td colSpan="7" className="px-4 py-6 text-center text-sm text-ink/40">No employees with gross salary configured</td></tr>}
                        {data.rows.map((r) => (
                            <tr key={r.employee_id} className="border-b border-ink/5">
                                <td className="px-3 py-2 text-ink/70">{r.employee_no || "—"}</td>
                                <td className="px-3 py-2 text-ink">{r.name}</td>
                                <td className="px-3 py-2 text-ink/70 hidden md:table-cell">{r.department}</td>
                                <td className="px-3 py-2 text-ink/70 hidden lg:table-cell">{r.category || "—"}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.monthly_gross)}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-amber-700">{fmt(r.itf_1pct)}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-klein">{fmt(r.nsitf_1pct)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function GLAReport() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [multiplier, setMultiplier] = useState(3);
    const [premiumRate, setPremiumRate] = useState(1);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reports/group-life-assurance", { params: { multiplier, premium_rate: premiumRate } });
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not generate report");
            setData(null);
        } finally { setLoading(false); }
    }, [multiplier, premiumRate]);

    useEffect(() => { load(); }, [load]);

    const onExport = () => {
        if (!data) return;
        const rows = data.rows.map((r) => [
            r.employee_no, r.name, r.department, r.gender, r.date_of_birth, r.joining_date, r.annual_gross, r.sum_assured, r.premium,
        ]);
        exportCSV(
            `group_life_assurance_${multiplier}x.csv`,
            ["Emp No", "Name", "Department", "Gender", "DOB", "Joined", "Annual Gross", "Sum Assured", "Premium"],
            rows,
        );
    };

    return (
        <div className="space-y-6" data-testid="gla-report">
            <div className="bg-white border border-ink/10 p-5 flex items-end gap-4 flex-wrap" data-testid="gla-controls">
                <div>
                    <label className="overline block mb-2">Multiplier (× annual gross)</label>
                    <input type="number" min="1" max="10" step="0.5" value={multiplier} onChange={(e) => setMultiplier(Number(e.target.value) || 3)} className="border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none w-32" data-testid="gla-multiplier" />
                </div>
                <div>
                    <label className="overline block mb-2">Premium rate (%)</label>
                    <input type="number" min="0" max="10" step="0.1" value={premiumRate} onChange={(e) => setPremiumRate(Number(e.target.value) || 1)} className="border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none w-32" data-testid="gla-premium-rate" />
                </div>
                <button onClick={load} disabled={loading} className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="gla-recalc">
                    {loading ? "Calculating…" : "Recalculate"}
                </button>
                {data && (
                    <button onClick={onExport} className="ml-auto inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink/5" data-testid="gla-export-btn">
                        <Download className="w-4 h-4" /> Export CSV
                    </button>
                )}
            </div>

            {data && (
                <>
                    <div className="p-4 rounded bg-bone/40 border border-ink/15 text-sm text-ink/70" data-testid="gla-note">
                        <strong className="text-ink">Compliance note:</strong> {data.note}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10" data-testid="gla-kpis">
                        <div className="bg-white p-5">
                            <div className="overline">Headcount</div>
                            <div className="font-display font-black text-3xl mt-2 tabular-nums">{data.headcount}</div>
                        </div>
                        <div className="bg-white p-5">
                            <div className="overline">Total annual gross</div>
                            <div className="font-display font-black text-3xl mt-2 tabular-nums">{fmt(data.total_annual_gross)}</div>
                        </div>
                        <div className="bg-white p-5">
                            <div className="overline">Total sum assured</div>
                            <div className="font-display font-black text-3xl mt-2 tabular-nums text-klein">{fmt(data.total_sum_assured)}</div>
                            <div className="text-[11px] text-ink/40 mt-1">{data.multiplier}× annual gross</div>
                        </div>
                        <div className="bg-white p-5">
                            <div className="overline">Annual premium</div>
                            <div className="font-display font-black text-3xl mt-2 tabular-nums text-terracotta">{fmt(data.total_annual_premium)}</div>
                            <div className="text-[11px] text-ink/40 mt-1">{data.premium_rate}% of sum assured</div>
                        </div>
                    </div>

                    <div className="bg-white border border-ink/10" data-testid="gla-by-gender">
                        <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg">Workforce composition</div>
                        <div className="grid grid-cols-3 gap-px bg-ink/10">
                            <div className="bg-white p-4 text-center">
                                <div className="overline">Male</div>
                                <div className="font-display font-black text-2xl mt-1 tabular-nums text-klein">{data.by_gender.male}</div>
                            </div>
                            <div className="bg-white p-4 text-center">
                                <div className="overline">Female</div>
                                <div className="font-display font-black text-2xl mt-1 tabular-nums text-terracotta">{data.by_gender.female}</div>
                            </div>
                            <div className="bg-white p-4 text-center">
                                <div className="overline">Unspecified</div>
                                <div className="font-display font-black text-2xl mt-1 tabular-nums text-ink/40">{data.by_gender.unspecified}</div>
                            </div>
                        </div>
                    </div>

                    {data.by_department.length > 0 && (
                        <div className="bg-white border border-ink/10" data-testid="gla-by-dept">
                            <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg">By department</div>
                            <table className="w-full text-sm">
                                <thead><tr className="border-b border-ink/10 bg-bone/40">
                                    <th className="text-left overline px-4 py-2">Department</th>
                                    <th className="text-right overline px-4 py-2">Sum assured</th>
                                    <th className="text-right overline px-4 py-2">Premium</th>
                                </tr></thead>
                                <tbody>
                                    {data.by_department.map((d) => (
                                        <tr key={d.department} className="border-b border-ink/5">
                                            <td className="px-4 py-2.5 text-ink">{d.department}</td>
                                            <td className="px-4 py-2.5 text-right tabular-nums text-klein">{fmt(d.sum_assured)}</td>
                                            <td className="px-4 py-2.5 text-right tabular-nums text-terracotta">{fmt(d.premium)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="bg-white border border-ink/10 overflow-x-auto" data-testid="gla-rows">
                        <div className="px-6 py-4 border-b border-ink/10 font-display font-bold text-lg">
                            Per-employee schedule <span className="text-ink/40 text-sm font-normal">· {data.rows.length}</span>
                        </div>
                        <table className="w-full text-sm border-collapse">
                            <thead><tr className="border-b-2 border-ink/20 bg-bone">
                                <th className="text-left overline px-3 py-3">Emp No</th>
                                <th className="text-left overline px-3 py-3">Name</th>
                                <th className="text-left overline px-3 py-3 hidden md:table-cell">Department</th>
                                <th className="text-left overline px-3 py-3 hidden lg:table-cell">DOB</th>
                                <th className="text-right overline px-3 py-3">Annual gross</th>
                                <th className="text-right overline px-3 py-3">Sum assured</th>
                                <th className="text-right overline px-3 py-3">Premium</th>
                            </tr></thead>
                            <tbody>
                                {data.rows.length === 0 && <tr><td colSpan="7" className="px-4 py-6 text-center text-sm text-ink/40">No active employees</td></tr>}
                                {data.rows.map((r) => (
                                    <tr key={r.employee_id} className="border-b border-ink/5">
                                        <td className="px-3 py-2 text-ink/70">{r.employee_no || "—"}</td>
                                        <td className="px-3 py-2 text-ink">{r.name}</td>
                                        <td className="px-3 py-2 text-ink/70 hidden md:table-cell">{r.department}</td>
                                        <td className="px-3 py-2 text-ink/70 hidden lg:table-cell">{r.date_of_birth || "—"}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{fmt(r.annual_gross)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-klein">{fmt(r.sum_assured)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-terracotta">{fmt(r.premium)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

